/*
  Event funnel (sections/event-funnel.liquid)
  Step 1 email -> Step 2 discount code -> Step 3 interest -> Step 4 done.
  All campaign data comes from data attributes on <event-funnel>; nothing is hardcoded here.
*/
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('event-funnel')) return;

  var STORAGE_PREFIX = 'jme_event_funnel:';
  var TOTAL_STEPS = 3;
  var GENERIC_ERROR = 'Something went wrong. Please try again.';
  var memoryStore = {};

  function nowIso() {
    return new Date().toISOString();
  }

  function slugify(label) {
    return String(label || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function parseDate(value) {
    if (!value) return null;
    // Liquid renders offsets as +0000; ISO parsers want +00:00.
    var normalized = String(value)
      .trim()
      .replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    var ms = Date.parse(normalized);
    return isNaN(ms) ? null : new Date(ms);
  }

  function designMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  // localStorage with an in-memory fallback (private mode, blocked storage, theme editor).
  var storage = {
    read: function (key) {
      if (!designMode()) {
        try {
          var raw = window.localStorage.getItem(key);
          if (raw) return JSON.parse(raw);
        } catch (error) {
          /* fall through to memory */
        }
      }
      return memoryStore[key] || null;
    },
    write: function (key, value) {
      memoryStore[key] = value;
      if (designMode()) return;
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        /* memory only */
      }
    },
    remove: function (key) {
      delete memoryStore[key];
      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        /* ignore */
      }
    }
  };

  function publish(campaignId, step, interestSlug) {
    var payload = { campaign_id: campaignId, step: step, interest_slug: interestSlug || null };
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('event_funnel', payload);
      }
    } catch (error) {
      /* analytics must never break the funnel */
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'event_funnel',
        campaign_id: campaignId,
        step: step,
        interest_slug: interestSlug || null
      });
    } catch (error) {
      /* ignore */
    }
  }

  function klaviyo(args) {
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') window.klaviyo.push(args);
    } catch (error) {
      /* ignore */
    }
  }

  // Stores with spam protection reject token-less posts with a 400 "Missing CAPTCHA token".
  // Remember that for the session so later submits go straight to the native path.
  var CAPTCHA_KEY = STORAGE_PREFIX + 'captcha';
  var captchaSeen = false;

  function captchaRequired() {
    if (captchaSeen) return true;
    try {
      return window.sessionStorage.getItem(CAPTCHA_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function rememberCaptcha() {
    captchaSeen = true;
    try {
      window.sessionStorage.setItem(CAPTCHA_KEY, '1');
    } catch (error) {
      /* memory only */
    }
  }

  // Shopify's storefront form bootstrap flags the forms it has bound to its captcha provider
  // (both forms carry data-shopify-captcha="true" so that happens on page load when spam
  // protection is on). A bound form must be submitted natively so the token gets attached.
  function formNeedsCaptcha(form) {
    var data = form.dataset;
    return data.cptcha === 'true' || data.hcaptchaBound === 'true' || data.recaptchaBound === 'true';
  }

  class EventFunnel extends HTMLElement {
    connectedCallback() {
      var self = this;
      // Activation is explicit (selectFunnels picks one event per page). The microtask only
      // covers an element added outside that flow, and never a hidden one.
      Promise.resolve().then(function () {
        if (!self.initialized && !self.hidden && self.isConnected) self.activate();
      });
    }

    activate() {
      if (this.initialized) return;
      this.initialized = true;

      this.campaignId = (this.dataset.campaignId || '').trim();
      this.discountCode = (this.dataset.discountCode || '').trim();
      this.shopUrl = this.dataset.shopUrl || '/collections/all';
      this.offerUrl = this.dataset.offerUrl || this.shopUrl;
      this.returnTo = this.dataset.returnTo || window.location.pathname;
      this.expiresAt = parseDate(this.dataset.offerExpiration);
      this.storageKey = STORAGE_PREFIX + this.campaignId;

      this.panels = toArray(this.querySelectorAll('[data-panel]'));
      this.progress = this.querySelector('[data-progress]');
      this.emailForm = this.querySelector('[data-funnel-form="email"]');
      this.interestForm = this.querySelector('[data-funnel-form="interest"]');
      this.currentStep = null;
      this.pendingOption = null;

      // State is keyed per campaign, so a different event always starts fresh while an
      // earlier event's progress survives (two events on one day, for example).
      this.state = storage.read(this.storageKey);
      if (!this.state || typeof this.state !== 'object') this.state = null;

      this.handlePostedRedirect();

      if (!this.state) this.state = { step: 1, startedAt: nowIso() };
      if (!this.state.startedAt) this.state.startedAt = nowIso();
      if (this.emailForm && this.emailForm.querySelector('[data-server-error]')) this.state.step = 1;
      this.saveState({});

      this.applyExpiration();
      this.bindEvents();
      this.showStep(this.state.step, { initial: true });
    }

    /* ---------- state ---------- */

    saveState(patch) {
      this.state = Object.assign({}, this.state, patch || {});
      storage.write(this.storageKey, this.state);
    }

    // Shopify redirects to return_to?customer_posted=true after a native customer form post.
    handlePostedRedirect() {
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (error) {
        return;
      }
      if (params.get('customer_posted') !== 'true') return;

      if (!this.state) {
        this.state = { step: 2, startedAt: nowIso(), emailSubmittedAt: nowIso() };
      } else if (Number(this.state.step) === 1) {
        this.state.step = 2;
        this.state.emailSubmittedAt = this.state.emailSubmittedAt || nowIso();
      }

      // Drop only customer_posted so ?event=<handle> (or campaign parameters) survive.
      try {
        params.delete('customer_posted');
        var query = params.toString();
        window.history.replaceState(null, document.title, window.location.pathname + (query ? '?' + query : ''));
      } catch (error) {
        /* leave the URL alone */
      }
    }

    /* ---------- steps ---------- */

    normalizeStep(step) {
      step = Number(step);
      if (!(step >= 1 && step <= TOTAL_STEPS + 1)) return 1;
      return step;
    }

    showStep(step, options) {
      options = options || {};
      step = this.normalizeStep(step);

      this.panels.forEach(function (panel) {
        panel.hidden = Number(panel.dataset.panel) !== step;
      });
      if (this.progress) {
        this.progress.textContent = step > TOTAL_STEPS ? 'All done' : 'Step ' + step + ' of ' + TOTAL_STEPS;
      }

      if (this.currentStep === step) return;
      this.currentStep = step;
      publish(this.campaignId, step, this.state.interestSlug || null);
      if (!options.initial) this.focusStep(step);
    }

    goTo(step) {
      this.saveState({ step: this.normalizeStep(step) });
      this.showStep(step);
    }

    focusStep(step) {
      var panel = this.panels.filter(function (candidate) {
        return Number(candidate.dataset.panel) === step;
      })[0];
      if (!panel) return;

      // Bring the new panel's top back into view (instant: smooth scrolling is unreliable
      // right after a layout change, and the CSS scroll-margin accounts for the sticky header).
      if (this.getBoundingClientRect().top < 0) {
        try {
          this.scrollIntoView({ block: 'start' });
        } catch (error) {
          this.scrollIntoView();
        }
      }

      var heading = panel.querySelector('[tabindex="-1"]');
      if (heading) {
        try {
          heading.focus({ preventScroll: true });
        } catch (error) {
          heading.focus();
        }
      }
    }

    reset() {
      storage.remove(this.storageKey);
      this.state = { step: 1, startedAt: nowIso() };
      this.saveState({});
      this.restoreForms();
      if (this.emailForm) this.emailForm.reset();
      this.showStep(1);
      var input = this.emailForm && this.emailForm.querySelector('input[name="contact[email]"]');
      if (input) input.focus();
    }

    /* ---------- offer ---------- */

    applyExpiration() {
      if (!this.expiresAt || this.expiresAt.getTime() > Date.now()) return;
      var shopUrl = this.shopUrl;
      toArray(this.querySelectorAll('[data-offer]')).forEach(function (box) {
        box.hidden = true;
      });
      toArray(this.querySelectorAll('[data-offer-ended]')).forEach(function (note) {
        note.hidden = false;
      });
      toArray(this.querySelectorAll('[data-shop-link]')).forEach(function (link) {
        link.setAttribute('href', shopUrl);
      });
    }

    copyCode(button) {
      var box = button.closest('[data-offer]') || this;
      var codeNode = box.querySelector('[data-code]');
      var code = this.discountCode || (codeNode ? codeNode.textContent.trim() : '');
      if (!code) return;

      var original = button.dataset.copyLabel || button.textContent.trim();
      button.dataset.copyLabel = original;
      var announce = function (text) {
        button.textContent = text;
        window.clearTimeout(button.copyTimer);
        button.copyTimer = window.setTimeout(function () {
          button.textContent = original;
        }, 2000);
      };
      var fallback = function () {
        if (codeNode) {
          try {
            var range = document.createRange();
            range.selectNodeContents(codeNode);
            var selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            if (document.execCommand && document.execCommand('copy')) {
              announce('Copied!');
              return;
            }
          } catch (error) {
            /* text stays selected for a manual copy */
          }
        }
        announce('Code selected');
      };

      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(code).then(function () {
          announce('Copied!');
        }, fallback);
      } else {
        fallback();
      }
    }

    /* ---------- events ---------- */

    bindEvents() {
      var self = this;

      if (this.emailForm) {
        this.emailForm.addEventListener('submit', function (event) {
          self.onEmailSubmit(event);
        });
      }

      if (this.interestForm) {
        this.interestForm.addEventListener('click', function (event) {
          var option = event.target.closest('[data-interest-option]');
          if (option) self.pendingOption = option;
        });
        this.interestForm.addEventListener('submit', function (event) {
          self.onInterestSubmit(event);
        });
      }

      this.addEventListener('click', function (event) {
        var copy = event.target.closest('[data-copy]');
        if (copy) {
          event.preventDefault();
          self.copyCode(copy);
          return;
        }
        var next = event.target.closest('[data-next]');
        if (next) {
          event.preventDefault();
          self.goTo(next.dataset.next);
          return;
        }
        var reset = event.target.closest('[data-reset]');
        if (reset) {
          event.preventDefault();
          self.reset();
        }
      });

      // Back/forward cache can restore a page mid-submission; put the forms back.
      window.addEventListener('pageshow', function (event) {
        if (event.persisted) self.restoreForms();
      });
    }

    onEmailSubmit(event) {
      var form = this.emailForm;
      if (form.dataset.nativeSubmit === 'true') return; // native fallback in progress
      event.preventDefault();

      var input = form.querySelector('input[name="contact[email]"]');
      var email = input ? input.value.trim() : '';
      if (!email) {
        if (input) input.focus();
        return;
      }

      var self = this;
      // Save the intended next state first so a reCAPTCHA round trip still lands on step 2.
      this.saveState({ step: 2, email: email, emailSubmittedAt: nowIso() });
      this.submitForm(form, {
        buttons: [form.querySelector('[type="submit"]')],
        onSuccess: function () {
          klaviyo(['identify', { email: email }]);
          self.showStep(2);
        },
        onFailure: function () {
          self.saveState({ step: 1 });
        }
      });
    }

    onInterestSubmit(event) {
      var form = this.interestForm;
      if (form.dataset.nativeSubmit === 'true') return;
      event.preventDefault();

      var option = (event.submitter && event.submitter.closest('[data-interest-option]')) || this.pendingOption;
      if (!option) return;

      var email = this.state.email;
      if (!email) {
        // Storage was unavailable; ask for the email again so the interest can be tagged.
        this.goTo(1);
        this.showError(this.emailForm, 'Please enter your email again to save your pick.');
        return;
      }

      var label = option.dataset.interestLabel || option.textContent.trim();
      var slug = option.dataset.interestSlug || slugify(label);
      var emailField = form.querySelector('[data-interest-email]');
      var tagsField = form.querySelector('[data-interest-tags]');
      if (emailField) emailField.value = email;
      if (tagsField) tagsField.value = 'event:' + this.campaignId + ', interest:' + slug;

      var self = this;
      var previous = {
        step: this.state.step,
        interest: this.state.interest,
        interestSlug: this.state.interestSlug,
        completedAt: this.state.completedAt
      };
      var buttons = toArray(form.querySelectorAll('[data-interest-option]'));
      buttons.forEach(function (button) {
        button.removeAttribute('aria-pressed');
      });
      option.setAttribute('aria-pressed', 'true');

      this.saveState({ step: 4, interest: label, interestSlug: slug, completedAt: nowIso() });
      this.submitForm(form, {
        buttons: buttons,
        busyButton: option,
        onSuccess: function () {
          klaviyo(['identify', { email: email }]);
          klaviyo(['track', 'Event Funnel Interest', { campaign_id: self.campaignId, interest: label }]);
          self.showStep(4);
        },
        onFailure: function () {
          option.removeAttribute('aria-pressed');
          self.saveState(previous);
        }
      });
    }

    /* ---------- submission ---------- */

    // Post with fetch; if Shopify answers with a captcha challenge or demands a captcha token
    // (or the network fails), fall back to a native submit so Shopify's own form handler can
    // attach the token, run any challenge, and redirect to return_to.
    submitForm(form, options) {
      var self = this;
      var buttons = (options.buttons || []).filter(Boolean);
      var busyButton = options.busyButton || buttons[0];
      this.setBusy(buttons, busyButton, true);
      this.showError(form, '');

      if (formNeedsCaptcha(form) || captchaRequired()) {
        this.nativeSubmit(form, buttons, busyButton);
        return;
      }

      var action = form.getAttribute('action') || '/contact';
      var request;
      try {
        request = window.fetch(action, {
          method: 'POST',
          body: new FormData(form),
          redirect: 'follow',
          credentials: 'same-origin',
          headers: { Accept: 'text/html,application/xhtml+xml,*/*' }
        });
      } catch (error) {
        request = Promise.reject(error);
      }

      request
        .then(
          function (response) {
            var url = response.url || '';
            if (url.indexOf('/challenge') !== -1) return { challenge: true };
            return response.text().then(function (html) {
              if (/shopify-challenge__/i.test(html)) return { challenge: true };
              if (!response.ok) {
                if (/captcha/i.test(html)) {
                  rememberCaptcha();
                  return { challenge: true };
                }
                return { failed: true };
              }
              var message = self.extractServerError(html);
              if (message) return { failed: true, message: message };
              return { ok: true };
            });
          },
          function () {
            return { challenge: true };
          }
        )
        .then(function (result) {
          if (result.ok) {
            self.setBusy(buttons, busyButton, false);
            options.onSuccess();
            return;
          }
          if (result.challenge) {
            self.nativeSubmit(form, buttons, busyButton);
            return;
          }
          self.setBusy(buttons, busyButton, false);
          self.showError(form, result.message || GENERIC_ERROR);
          if (options.onFailure) options.onFailure();
        })
        .catch(function () {
          self.setBusy(buttons, busyButton, false);
          self.showError(form, GENERIC_ERROR);
          if (options.onFailure) options.onFailure();
        });
    }

    nativeSubmit(form, buttons, busyButton) {
      var self = this;
      var submitted = false;
      form.dataset.nativeSubmit = 'true';

      var submitNow = function () {
        if (submitted) return;
        submitted = true;
        try {
          // requestSubmit fires the submit event, so Shopify's captcha handler (reCAPTCHA or hCaptcha) can attach its token.
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }
        } catch (error) {
          try {
            form.submit();
          } catch (fallbackError) {
            delete form.dataset.nativeSubmit;
            self.setBusy(buttons, busyButton, false);
            self.showError(form, GENERIC_ERROR);
          }
        }
      };

      // Shopify's bootstrap drops the submit of a protected form it has not bound yet, so if it
      // is present but has not touched this form, ask it to bind first, then submit.
      var captcha = window.Shopify && window.Shopify.captcha;
      if (captcha && typeof captcha.protect === 'function' && !formNeedsCaptcha(form)) {
        try {
          captcha.protect(form, submitNow);
          window.setTimeout(submitNow, 4000);
          return;
        } catch (error) {
          /* fall through to a plain submit */
        }
      }
      submitNow();
    }

    extractServerError(html) {
      if (!html || html.indexOf('data-server-error') === -1) return '';
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-server-error]');
        var text = node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
        return text || 'Please check your email address and try again.';
      } catch (error) {
        return 'Please check your email address and try again.';
      }
    }

    setBusy(buttons, busyButton, busy) {
      buttons.forEach(function (button) {
        button.disabled = busy;
        if (button !== busyButton) return;
        if (busy) {
          button.dataset.label = button.textContent.trim();
          button.textContent = 'Saving…';
          button.setAttribute('aria-busy', 'true');
        } else {
          if (button.dataset.label) button.textContent = button.dataset.label;
          button.removeAttribute('aria-busy');
        }
      });
    }

    showError(form, message) {
      if (!form) return;
      var node = form.querySelector('[data-form-error]');
      if (!node) return;
      node.textContent = message || '';
      node.hidden = !message;
    }

    restoreForms() {
      var self = this;
      [this.emailForm, this.interestForm].forEach(function (form) {
        if (!form) return;
        delete form.dataset.nativeSubmit;
        var buttons = toArray(form.querySelectorAll('button'));
        self.setBusy(buttons, null, false);
        buttons.forEach(function (button) {
          if (button.dataset.label) button.textContent = button.dataset.label;
          button.removeAttribute('aria-busy');
          button.removeAttribute('aria-pressed');
        });
        self.showError(form, '');
      });
    }
  }

  var SELECTED_KEY = STORAGE_PREFIX + 'selected';

  function eventParam() {
    try {
      return (new URLSearchParams(window.location.search).get('event') || '').trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }

  // Show exactly one <event-funnel> per page: the one named by ?event=<handle>, else the one
  // this visitor last opened (so the reload after a native form post returns to it), else the
  // server-side default (the pinned entry or the first active entry).
  function selectFunnels(scope) {
    var funnels = toArray((scope || document).querySelectorAll('event-funnel'));
    if (!funnels.length) return;

    var byHandle = function (handle) {
      if (!handle) return null;
      handle = String(handle).toLowerCase();
      return (
        funnels.filter(function (funnel) {
          return (funnel.dataset.eventHandle || '').toLowerCase() === handle;
        })[0] || null
      );
    };
    var remembered = storage.read(SELECTED_KEY);
    var chosen =
      byHandle(eventParam()) ||
      byHandle(typeof remembered === 'string' ? remembered : '') ||
      funnels.filter(function (funnel) {
        return funnel.dataset.default === 'true';
      })[0] ||
      funnels[0];

    funnels.forEach(function (funnel) {
      funnel.hidden = funnel !== chosen;
    });
    if (chosen.dataset.eventHandle) storage.write(SELECTED_KEY, chosen.dataset.eventHandle);
    chosen.activate();
  }

  window.customElements.define('event-funnel', EventFunnel);
  selectFunnels(document);
  document.addEventListener('shopify:section:load', function (event) {
    selectFunnels(event.target);
  });
})();
